
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { useAuth } from "@/hooks/useAuth";
import { formatDateForDatabase } from "@/hooks/goal-operations/helpers/dateUtils";
import { useCallback } from "react";
import { logger } from '@/utils/logger';

export const useTodoAddMutation = () => {
  const { handleError, handleSuccess } = useErrorHandler();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const addTodoMutation = useMutation({
    mutationFn: async ({ goalTitle, todoText, todoDueDate }: { goalTitle: string, todoText: string, todoDueDate: string }) => {
      if (!user) throw new Error("User must be authenticated");
      
      logger.debug(`[useTodoAddMutation] START - Text: "${todoText}" | Goal: "${goalTitle}" | Date: "${todoDueDate}"`);
      
      if (!todoText) {
        logger.error(`[useTodoAddMutation] Empty todo text`);
        throw new Error("Todo text cannot be empty");
      }
      
      if (todoText === goalTitle) {
        logger.warn(`[useTodoAddMutation] todoText equals goalTitle - possible parameter ordering issue`);
      }
      
      const formattedDueDate = formatDateForDatabase(todoDueDate);

      const { data: goal, error: goalError } = await supabase
        .from('goals')
        .select('id')
        .eq('title', goalTitle)
        .eq('user_id', user.id)
        .single();

      if (goalError) {
        logger.error(`[useTodoAddMutation] Error fetching goal:`, goalError);
        throw goalError;
      }

      if (!goal) {
        logger.error(`[useTodoAddMutation] Goal not found for title: "${goalTitle}"`);
        throw new Error("Goal not found");
      }

      const todoToCreate = {
        goal_id: goal.id,
        user_id: user.id,
        text: todoText,
        due_date: formattedDueDate,
        completed: false
      };
      
      const { data: todo, error: insertError } = await supabase
        .from('todos')
        .insert([todoToCreate])
        .select()
        .single();

      if (insertError) {
        logger.error(`[useTodoAddMutation] Error inserting todo:`, insertError);
        throw insertError;
      }
      
      logger.debug(`[useTodoAddMutation] Successfully created todo: "${todo.text}"`);
      return todo;
    },
    onSuccess: (data) => {
      logger.debug(`[useTodoAddMutation] onSuccess: "${data.text}"`);
      queryClient.invalidateQueries({ queryKey: ["goals"] });
      handleSuccess("New todo item has been added to your goal.", { title: "Todo added" });
    },
    onError: (error: Error) => {
      logger.error(`[useTodoAddMutation] onError:`, error);
      handleError(error, { title: "Error adding todo" });
    }
  });

  const addTodo = useCallback((params: { goalTitle: string, todoText: string, todoDueDate: string }) => {
    logger.debug(`[useTodoAddMutation] addTodo called:`, params);
    
    if (!params.todoText) {
      logger.error(`[useTodoAddMutation] Empty todo text`);
      return;
    }
    
    if (params.todoText === params.goalTitle) {
      logger.warn(`[useTodoAddMutation] todoText equals goalTitle`);
    }
    
    return addTodoMutation.mutate(params);
  }, [addTodoMutation]);

  return { addTodo };
};
